<template>
  <div class="main-layout">

    <!-- Sponsor Banner Sidebar -->
    <aside v-if="showSidebar" class="sidebar hide-mobile">
      <div class="sidebar-content">
        <SponsorSidebar />
      </div>
    </aside>

    <main
      class="main-content"
      :class="{ 
        'with-sidebar': showSidebar, 
        'is-chat-view': isChatPage,
        'no-nav': !authStore.isAuthenticated 
      }"
    >
      <div class="content-wrapper">
        <RouterView v-slot="{ Component }">
          <Transition name="page" mode="out-in">
            <component :is="Component" v-if="Component" />
          </Transition>
        </RouterView>
      </div>
      <AppFooter />
    </main>

    <!-- Mobile Sponsor Footer -->
    <footer v-if="showSidebar" class="mobile-footer hide-desktop">
      <div class="mobile-footer-content">
        <h4 class="footer-title">Global Sponsors</h4>
        <SponsorSidebar horizontal />
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from "vue";
import { RouterView, useRoute } from "vue-router";
import AppFooter from "@/components/ui/AppFooter.vue";
import SponsorSidebar from "@/components/ui/SponsorSidebar.vue";
import { useAuthStore } from "@/stores/auth.store";
import { useBannersAdminStore } from "@/stores/admin/banners.admin.store";

const route = useRoute();
const authStore = useAuthStore();
const bannersStore = useBannersAdminStore();

onMounted(() => {
  bannersStore.loadBanners();
});

// Show sidebar for authenticated users (will house sponsor banners)
const showSidebar = computed(() => authStore.isAuthenticated);

// Special case for chat to prevent browser scrolling
const isChatPage = computed(() => route.path.startsWith("/chat"));
</script>

<style scoped>
.main-layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.sidebar {
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  width: var(--sidebar-width);
  padding-top: calc(var(--nav-height) + var(--spacing-sm));
  padding-left: var(--spacing-xs);
  padding-right: var(--spacing-xs);
  z-index: var(--z-sticky);
}

.sidebar-content {
  height: 100%;
  padding: 4px;
  overflow-y: auto; /* Allow scrolling if 5 banners are too tall, although they should fit */
}

/* Subtle scrollbar for sidebar */
.sidebar-content::-webkit-scrollbar {
  width: 4px;
}

.sidebar-content::-webkit-scrollbar-thumb {
  background: var(--color-border-strong);
  border-radius: var(--radius-full);
}

.main-content {
  flex: 1;
  padding-top: var(--nav-height);
  padding-bottom: var(--spacing-2xl);
  transition: padding-left var(--transition-normal);
}

.main-content.no-nav {
  padding-top: 0;
}

.main-content.is-chat-view {
  padding-bottom: 0px !important;
}

.main-content.with-sidebar {
  padding-left: var(--sidebar-width);
}

.content-wrapper {
  max-width: var(--content-max-width);
  margin: 0 auto;
  padding: 0 var(--spacing-md);
}

/* Page transitions */
.page-enter-active,
.page-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.page-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.page-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Mobile adjustments */
@media (max-width: 768px) {
  .main-content {
    padding-left: 0 !important;
    padding-top: var(--nav-height);
  }

  .main-content.no-nav {
    padding-top: 0;
  }

  .content-wrapper {
    padding: 0 var(--spacing-sm);
  }

  .mobile-footer {
    padding: var(--spacing-lg) var(--spacing-md);
    background: var(--color-bg-secondary);
    border-top: 1px solid var(--color-border);
    margin-top: auto;
  }

  .mobile-footer-content {
    max-width: var(--content-max-width);
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md);
  }

  .footer-title {
    font-size: var(--font-size-sm);
    color: var(--color-text-muted);
    text-align: center;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin: 0;
  }
}
</style>
